import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './Modal.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  hideCloseButton?: boolean;
  actionButtons?: React.ReactNode;
  headerContent?: React.ReactNode; // New prop for content above action buttons
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, hideCloseButton = false, actionButtons, headerContent }) => {
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      // Add modal-open class to body to handle z-index issues
      document.body.classList.add('modal-open');
      
      // Handle escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      
      return () => {
        // Restore body scroll and remove event listener when modal closes
        document.body.style.overflow = 'unset';
        document.body.classList.remove('modal-open');
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content">
        <div className={`modal-header ${!title && !headerContent ? 'modal-header-compact' : ''}`}>
          {headerContent && <div className="modal-header-content">{headerContent}</div>}
          {title && <h2 className="modal-title">{title}</h2>}
          <div className="modal-header-actions">
            {actionButtons}
            {!hideCloseButton && (
              <button className="modal-close" onClick={onClose}>
                <X size={20} />
              </button>
            )}
          </div>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );

  // Render modal using portal to body element
  return createPortal(modalContent, document.body);
};
